import React, { Dispatch, FormEvent, SetStateAction, useEffect, useState, useMemo } from 'react';
import { gql, useMutation } from '@apollo/client';

import styles from './addModal.module.scss';

import Modal from '../../../../components/modal/Modal';
import { TextInput, TextInputValue } from '../../../../components/text-input/TextInput';
import Button from '../../../../components/button/Button';
import Checkbox from '../../../../components/checkbox/Checkbox';
import LocalConfirmation from '../../../../components/local-confirmation/LocalConfirmation';
import { isEmptyObject, validateEmail, validateIsEmpty } from '../../../../utils/validation';
import { addDays, formatDate } from '../../../../utils/time';
import { HEAUser } from '../../../../types';
import DeleteIcon from '../../../../assets/icons/people_delete.svg?react';
import { logger } from '../../../../utils/logger';
import { useToast } from '@src/hooks';

type Props = {
  open: boolean;
  onClose: () => void;
  user?: HEAUser;
  onCompleted: () => void;
};

const emptyErrors = {
  firstName: '',
  lastName: '',
  email: ''
};

type InviteUserInput = {
  firstName: string;
  lastName: string;
  email: string;
  association?: string;
  notes?: string;
  expireAt?: string;
}

type UpdateUserInput = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  expireAt?: string;
  expired?: boolean;
}

type InviteUserResponse = {
  heaInviteUser: boolean;
}

type UpdateUserResponse = {
  heaUpdateUser: boolean;
}

type DeleteUserResponse = {
  heaDeleteUser: boolean;
}

const inviteMutation = gql`
  mutation heaInviteUser($input: HEAInviteUserInput!) {
    heaInviteUser(input: $input)
  }
`;

const updateMutation = gql`
  mutation heaUpdateUser($input: HEAUpdateUserInput!) {
    heaUpdateUser(input: $input)
  }
`;

const deleteMutation = gql`
  mutation heaDeleteUser($input: IDInput!) {
    heaDeleteUser(input: $input)
  }
`;

const today = formatDate(addDays(new Date(), 1).valueOf(), 'yyyy-MM-dd');

function AddModal(props: Props) {
  const { open, onClose, user, onCompleted } = props;
  const { successToast, errorToast } = useToast();

  const [inviteUser, { loading: inviteLoading }] = useMutation<InviteUserResponse, { input: InviteUserInput }>(inviteMutation, {
    onError(e) {
      setBeError(e.message);
    },
    onCompleted(response) {
      if (response.heaInviteUser) {
        onCompleted();
        onClose();
        clearFields();
      }
    }
  });

  const [updateUser, { loading: editLoading }] = useMutation<UpdateUserResponse, { input: UpdateUserInput }>(updateMutation, {
    onError(e) {
      setBeError(e.message);
    },
    onCompleted(response) {
      if (response.heaUpdateUser) {
        onCompleted();
        onClose();
        clearFields();
      }
    }
  });

  const [deleteUser, { loading: deleteLoading }] = useMutation<DeleteUserResponse>(deleteMutation, {
    onError(err) {
      setBeError(err.message);
      errorToast("Delete failed, Please try again!");
    },
    onCompleted(response) {
      if (response.heaDeleteUser) {
        successToast("User successfully deleted!");
        onCompleted();
        onClose();
        clearFields();
      }
    },
  });

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [association, setAssociation] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [expireAt, setExpireAt] = useState<string>('');
  const [expired, setExpired] = useState(false);

  const [error, setError] = useState(emptyErrors);
  const [beError, setBeError] = useState('');
  const isEdit = !!user;

  const [removeConfirmation, setRemoveConfirmation] = useState(false);

  useEffect(() => {
    setFirstName(user?.firstName ?? '');
    setLastName(user?.lastName ?? '');
    setEmail(user?.email ?? '');
    setAssociation(user?.association ?? '');
    setNotes('');
    setExpireAt(user?.expireAt ? user.expireAt.split(' ')[0] : '');
    setExpired(user?.expired ?? false);
  }, [user]);

  useEffect(() => {
    if (open && !expireAt && user?.expireAt) {
      const expireDate = parseInt(user?.expireAt ? user?.expireAt.split(' ')[0] : '');
      if (expireDate) {
        setExpireAt(formatDate(expireDate, 'yyyy-MM-dd'));
      }
    }
  }, [user, open, expireAt])

  const handleChangeText = (setter: Dispatch<SetStateAction<string>>, key?: keyof typeof emptyErrors) => (value: TextInputValue) => {
    setter(value as string);
    if (key) {
      setError({
        ...error,
        [key]: '',
      });
    }
  };

  const clearFields = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setAssociation('');
    setNotes('');
    setExpireAt('');
    setExpired(false);
    setBeError('');
    setError(emptyErrors);
  };

  const handleClose = () => {
    clearFields();
    onClose();
  };

  const validate = () => {
    const errors: any = {};
    if (validateIsEmpty(firstName)) {
      errors.firstName = 'First name must not be empty.';
    }
    if (validateIsEmpty(lastName)) {
      errors.lastName = 'Last name must not be empty.';
    }
    const valid = validateEmail(email);
    if (valid) {
      errors.email = valid;
    }
    return errors;
  }

  const handleEdit = (e: FormEvent) => {
    e.preventDefault();
    const errors = validate();
    if (isEmptyObject(errors)) {
      setBeError('');
      updateUser({
        variables: {
          input: {
            id: user!.id,
            firstName,
            lastName,
            email,
            expireAt: expireAt ? new Date(expireAt).valueOf().toString() : '',
            expired
          }
        }
      });
    } else {
      logger.debug(`[e]`, errors);
      setError(errors);
    }
  };

  const handleSave = (e: FormEvent) => {
    e.preventDefault();
    const errors = validate();
    if (isEmptyObject(errors)) {
      setBeError('');
      inviteUser({
        variables: {
          input: {
            firstName,
            lastName,
            email,
            association,
            notes,
            expireAt: expireAt ? new Date(expireAt).valueOf().toString() : ''
          }
        }
      });
    } else {
      logger.debug(`[e]`, errors);
      setError(errors);
    }
  };

  const handleChangeExpired = (value: boolean) => setExpired(value);

  const isActive = useMemo(() => {
    if (!user) return false;
    const now = new Date().valueOf();
    if (!user.expireAt || Number(user.expireAt) > now) {
      return true;
    }
    return false;
  }, [user]);

  const handleRemove = () => {
    user && deleteUser({
      variables: {
        input: { id: user.id }
      }
    });
  };

  const handleConfirmRemove = () => {
    handleRemove();
    setRemoveConfirmation(false);
  };

  const handleCancel = () => {
    setRemoveConfirmation(false);
  };

  const handleShowConfirm = () => {
    setRemoveConfirmation(true);
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div className={styles.title}>{isEdit ? 'Edit User' : 'Invite User'}</div>
      {isEdit &&
        <div className={styles.deleteWrapper}>
          <LocalConfirmation onConfirm={handleConfirmRemove} onCancel={handleCancel} open={removeConfirmation}>
            <Button disabled={editLoading || deleteLoading} type={'button'} onClick={handleShowConfirm}
                className={styles.removeBtn}
                color={'red'}
                size={'extraSmall'}
                label={'Delete user'}
                leftIcon={<DeleteIcon/>}/>
          </LocalConfirmation>
        </div>
      }
      <form className={styles.form} onSubmit={isEdit ? handleEdit : handleSave}>
        <div className={styles.content}>
          <TextInput hasError={!!error.firstName} subTitle={error.firstName}
            placeholder={'First name'}
            inline={true}
            label={'First Name'}
            id={'firstName'}
            value={firstName}
            onChange={handleChangeText(setFirstName)}
          />
          <TextInput hasError={!!error.lastName} subTitle={error.lastName}
            placeholder={'Last name'}
            inline={true}
            label={'Last Name'}
            id={'lastName'}
            value={lastName}
            onChange={handleChangeText(setLastName)}
          />
          <TextInput allowSpecialCharacters hasError={!!error.email} subTitle={error.email}
            placeholder={'Email'}
            inline={true}
            label={'Email'}
            id={'email'}
            value={email}
            onChange={handleChangeText(setEmail)}
          />
          <TextInput
            placeholder={'Association'}
            inline={true}
            label={'Association'}
            id={'association'}
            value={association}
            onChange={handleChangeText(setAssociation)}
            readonly={isEdit}
          />
          {isEdit ? null : <TextInput
            placeholder={'Add notes'}
            inline={true}
            label={'Add Notes'}
            id={'notes'}
            value={notes}
            onChange={handleChangeText(setNotes)}
            textArea={true}
          />}
          <TextInput
            inline={true}
            label={'End Date'}
            id={'expireAt'}
            value={expireAt}
            onChange={handleChangeText(setExpireAt)}
            type={'date'}
            min={today}
          />
          {isEdit ? <>
            <div className={styles.row}>
              <div/>
              <div>
                <Checkbox id={'expired'} checked={expired} onChange={handleChangeExpired} label={'Expire Account'}/>
                <div className={styles.courseAction}>
                  <div className={`${styles.status} ${isActive ? styles.activeStatus : ''}`}>
                    {isActive ? 'Active' : 'Expired'}
                  </div>
                </div>
              </div>
            </div>
          </> : null}
          {Boolean(beError) && <div className={styles.error}>{beError}</div>}
          <div className={styles.action}>
            <div/>
            {isEdit ?
              <Button disabled={editLoading || deleteLoading} type={'submit'} label={'Save'}/> :
              <Button disabled={inviteLoading} type={'submit'} label={'Invite User'}/>
            }
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default AddModal;
